import { Link } from "react-router-dom";
import { useRuntimeConfig } from "../config/RuntimeConfigContext.jsx";
import { useSession } from "../auth/SessionContext.jsx";
import { ENVIRONMENT_LABELS_TH } from "../constants/environment.js";
import Sidebar from "./Sidebar.jsx";
import "./shell.css";

/**
 * Shared enterprise shell every guarded backoffice screen mounts inside:
 * sidebar navigation + topbar (brand on mobile, section crumb, environment,
 * sign-out) + main content + footer. `crumbs` describes which section of the
 * app this is - distinct from the hierarchy pages' own Breadcrumb, which tracks
 * the real Super Agent/PLC/Agent drill-down path.
 *
 * A crumb is either a plain string (e.g. ["Backoffice", "แดชบอร์ด"]) or
 * `{ label, to }`, which renders as a real link. The linked form exists because
 * a screen reached from another screen's row menu has no other way back: open
 * its URL directly and the browser has no history to go back to, so the trail
 * that says where you are has to be the way out.
 *
 * EVERY crumb carrying a `to` is a link, the last one included (owner: "ต้องกด
 * ได้ทุกอัน"). The last crumb still gets `aria-current="page"`, so a screen
 * reader is told it is the current page even though it is clickable - the two
 * are independent, and a self-link is a plain reload rather than a trap. A crumb
 * with no `to` stays plain text.
 *
 * At narrow widths the sidebar becomes a horizontal nav rail and this
 * topbar switches to a compact brand mark + icon-only sign-out so nothing
 * wraps into broken multi-line blocks.
 */
const crumbLabel = (crumb) => (typeof crumb === "string" ? crumb : crumb.label);

export default function AppShell({ crumbs = [], children }) {
  const hasLinkedCrumb = crumbs.some((crumb) => typeof crumb !== "string" && Boolean(crumb.to));
  const config = useRuntimeConfig();
  const { signOut } = useSession();
  const environmentLabel = ENVIRONMENT_LABELS_TH[config.environment] ?? config.environment;
  const isProduction = config.environment === "production";

  return (
    <div className="wx-app-shell">
      <Sidebar />
      <header className="wx-shell-topbar">
        <div className="wx-shell-topbar-brand">
          <span className="wx-shell-topbar-mark" aria-hidden="true">
            WX
          </span>
          <span className="wx-shell-topbar-name">Workmate X</span>
        </div>

        {crumbs.length > 0 &&
          (hasLinkedCrumb ? (
            <nav className="wx-shell-crumb" aria-label="เส้นทางหน้าปัจจุบัน">
              {crumbs.map((crumb, index) => {
                const label = crumbLabel(crumb);
                const to = typeof crumb === "string" ? null : crumb.to;
                const isLast = index === crumbs.length - 1;
                return (
                  <span key={`${label}-${index}`}>
                    {index > 0 && <span aria-hidden="true"> / </span>}
                    {to ? (
                      <Link
                        className="wx-shell-crumb-link"
                        to={to}
                        aria-current={isLast ? "page" : undefined}
                      >
                        {label}
                      </Link>
                    ) : (
                      <span aria-current={isLast ? "page" : undefined}>{label}</span>
                    )}
                  </span>
                );
              })}
            </nav>
          ) : (
            // Every other screen passes plain strings, and this renders exactly
            // the single text node it always did. Splitting a plain trail into
            // one element per segment would change nothing a visitor can see and
            // break every assertion that reads the trail as one string - a wide
            // blast radius bought for no benefit.
            <div className="wx-shell-crumb">{crumbs.map(crumbLabel).join(" / ")}</div>
          ))}

        <div className="wx-shell-topbar-actions">
          <span
            className={`wx-badge wx-shell-env-badge ${isProduction ? "wx-badge--success" : "wx-badge--warning"}`}
            title="สภาพแวดล้อมการทำงานปัจจุบัน"
          >
            {environmentLabel}
          </span>
          <button type="button" className="wx-shell-signout" onClick={signOut} aria-label="ออกจากระบบ">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <path d="M16 17l5-5-5-5" />
              <path d="M21 12H9" />
            </svg>
            <span className="wx-shell-signout-text">ออกจากระบบ</span>
          </button>
        </div>
      </header>

      <main className="wx-shell-main">{children}</main>

      <footer className="wx-shell-footer">
        <span>เวอร์ชัน {config.version}</span>
        <span>
          ติดต่อฝ่ายปฏิบัติการ:{" "}
          <a href={`mailto:${config.supportContact}`}>{config.supportContact}</a>
        </span>
      </footer>
    </div>
  );
}
